import { useEffect, useMemo, useState } from 'react';

import { ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import {
  Form,
  IconButton,
  MultiSelect,
  RyeconCaretLeft,
  RyeconClose,
  useZodForm,
} from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { NodeFilters, useFilterContext, zNodeFilters } from '@/context/FilterProvider';
import { usePanel } from '@/context/PanelProvider';
import { useTags } from '@/hooks/discovery/tags';
import { useCurrentPage } from '../Page';
import { AdvancedFilters } from './AdvancedFilters';
import { FilterSection } from './FilterSection';
import { ModelLevelFilters } from './ModelLevelFilters';
import { TypeFilters } from './TypeFilters';

const tagsName = 'tags' satisfies keyof NodeFilters;
const TagFilters = () => {
  const { environmentId } = useDbtCloudProject();
  const filterCtx = useFilterContext();

  const [loadTags, setLoadTags] = useState(false);

  const [uniqueTags] = useTags({
    environmentId,
    pause: !loadTags,
  });

  return (
    <FilterSection title="Tags">
      <div
        onFocus={(e) => {
          e.preventDefault();
          setLoadTags(true);
        }}
      >
        <MultiSelect
          className="mt-2"
          name={tagsName}
          options={useMemo(
            () =>
              uniqueTags?.data?.environment?.applied?.tags?.map((tag) => ({
                label: tag?.name,
                value: tag?.name || -1,
              })) || [],
            [uniqueTags],
          )}
          isEdit
          onChange={(selectedOptions) => {
            const tags = filterCtx.filters[tagsName] ?? [];
            const values = selectedOptions.map((option) => option.value.toString());
            const itemsToAdd = values.filter((tag) => !tags.includes(tag));
            const itemsToDelete = tags.filter((tag) => !values.includes(tag));

            const isAddOperation = true;
            if (itemsToAdd.length) {
              filterCtx.updateFilters({ [tagsName]: itemsToAdd }, isAddOperation);
            }
            if (itemsToDelete.length) {
              filterCtx.updateFilters({ [tagsName]: itemsToDelete }, !isAddOperation);
            }
          }}
        />
      </div>
    </FilterSection>
  );
};

export const ResourceFilterPanel = () => {
  const panel = usePanel();
  const filterCtx = useFilterContext();
  const { currentPage } = useCurrentPage();
  const isProjectLineage = currentPage === 'projectLineage';

  // using ResourceType for panel status is probably fine, but we might
  // need more options in the future
  const [type, setType] = useState<ResourceTypeExplorer | null>(null);

  // @ts-ignore
  const formMethods = useZodForm(zNodeFilters);
  const tagsFilter = filterCtx.filters[tagsName];
  useEffect(() => {
    formMethods.setValue(
      tagsName,
      (tagsFilter ?? []).map((tag) => ({ label: tag, value: tag })),
    );
  }, [tagsFilter, formMethods]);

  return (
    <Form formMethods={formMethods} onSubmit={() => {}}>
      {!type && (
        <div
          data-testid="filter-panel"
          className="h-full w-[265px] border-l border-borderMuted bg-bgMain text-fgMain"
        >
          <h2 className="border-b border-borderMuted px-6 py-2 text-sm font-normal leading-6">
            <span className="flex-0 flex space-x-1">
              <span className="flex-0 flex w-full align-middle">Filters</span>
              {isProjectLineage && (
                <IconButton
                  size="sm"
                  ryecon={RyeconClose}
                  className="flex-1 p-1 align-middle"
                  label="Close"
                  tooltip="Close"
                  onClick={() => {
                    panel.toggleFilterPanel(false);
                  }}
                />
              )}
            </span>
          </h2>
          <div className="m-6 text-base">
            <TypeFilters onDive={setType} />
            <TagFilters />
            <AdvancedFilters />
          </div>
        </div>
      )}

      {type === 'model' && (
        <div className="h-full w-[265px] border-l border-borderMuted bg-bgMain text-fgMain">
          <h2 className="border-b border-borderMuted px-6 py-2 text-sm font-normal leading-6">
            <span className="flex-0 flex space-x-1">
              <IconButton
                size="xs"
                className="flex-1 p-1 align-middle"
                ryecon={RyeconCaretLeft}
                onClick={() => {
                  setType(null);
                }}
              />
              <span className="flex-0 flex w-full align-middle">Models</span>
            </span>
          </h2>
          <div className="m-6 text-base">
            <ModelLevelFilters />
          </div>
        </div>
      )}
    </Form>
  );
};
